import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import type { FinanceSetupWizard, FinanceSetupStep } from '@/types/finance';

const STEP_LINKS: Record<string, string> = {
  ORG_SETTINGS: '/finance/setup',
  FISCAL_YEAR: '/finance/setup',
  CHART_OF_ACCOUNTS: '/finance/accounting',
  TAXES: '/finance/taxes',
  CURRENCIES: '/finance/setup',
  BANK_ACCOUNTS: '/finance/banking',
  OPENING_BALANCES: '/finance/accounting',
  USERS_ROLES: '/finance/administration',
  GO_LIVE: '/finance',
};

export function FinanceSetupWizardPage() {
  const navigate = useNavigate();
  const [wizard, setWizard] = useState<FinanceSetupWizard | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = () => {
    financeApi.setupWizard().then(setWizard).catch((e) => showApiError(e, 'Failed to load wizard'));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!wizard) return;
    const idx = wizard.steps.findIndex((s) => !s.completed);
    setActiveStep(idx >= 0 ? idx : wizard.steps.length - 1);
  }, [wizard]);

  const step = wizard?.steps[activeStep];

  const markComplete = async (s: FinanceSetupStep) => {
    setBusy(true);
    try {
      const updated = await financeApi.completeSetupStep(s.stepCode);
      setWizard(updated);
      notify.success(`${s.stepName} marked complete`);
      if (s.stepCode === 'GO_LIVE') {
        notify.success('Finance is live!');
        navigate('/finance');
      }
    } catch (e) {
      showApiError(e, 'Failed to save progress');
    } finally {
      setBusy(false);
    }
  };

  if (!wizard) {
    return <p className="p-6 text-slate-500">Loading setup wizard…</p>;
  }

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Finance setup wizard"
        description="Get your books ready in nine guided steps — no accounting degree required"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Finance', to: '/finance' },
          { label: 'Administration', to: '/finance/control' },
          { label: 'Setup wizard' },
        ]}
      />

      <div className="mb-6 flex items-center justify-between text-sm">
        <span className="text-slate-600">
          Progress: {wizard.completedCount} / {wizard.totalSteps} steps
        </span>
        <div className="h-2 w-40 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full bg-primary-500 transition-all"
            style={{ width: `${(wizard.completedCount / wizard.totalSteps) * 100}%` }}
          />
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {wizard.steps.map((s, i) => (
          <button
            key={s.stepCode}
            type="button"
            onClick={() => setActiveStep(i)}
            className={`rounded-full px-3 py-1 text-xs font-medium border ${
              i === activeStep
                ? 'border-primary-500 bg-primary-50 text-primary-800'
                : s.completed
                  ? 'border-green-200 bg-green-50 text-green-800'
                  : 'border-slate-200 text-slate-500'
            }`}
          >
            {i + 1}. {s.stepName.split(' ')[0]}
          </button>
        ))}
      </div>

      {step && (
        <div className="card p-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold">{step.stepName}</h3>
            <p className="text-sm text-slate-600 mt-1">{step.description}</p>
          </div>
          {step.completed && (
            <p className="text-sm text-green-700">Completed{step.completedAt ? ` on ${step.completedAt.slice(0, 10)}` : ''}</p>
          )}
          <div className="flex flex-wrap gap-2">
            {STEP_LINKS[step.stepCode] && (
              <ButtonLink to={STEP_LINKS[step.stepCode]} variant="secondary" size="sm">
                Open settings
              </ButtonLink>
            )}
            {!step.completed && (
              <Button loading={busy} onClick={() => void markComplete(step)}>
                Mark step complete
              </Button>
            )}
            {activeStep < wizard.steps.length - 1 && (
              <Button variant="secondary" onClick={() => setActiveStep((i) => i + 1)}>
                Next step
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
